import { FeatureFlag, Size } from '@ps/web-core-enums';
import type { RecurringTournament } from '@ps/web-shared-api-types/v0/SwHomeGames';
import { Input, Translation, useScreenSize } from '@ps/web-shared-components';
import { add, format, isSameDay } from 'date-fns';
import React, { useContext, useEffect, useMemo } from 'react';
import { useField, useForm } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import CoachMark from '@swf/components/CoachMark/CoachMark';
import StarsDropDown from '@swf/components/Inputs/StarsDropDown';
import useFeature from '@swf/core/hooks/useFeature';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import {
  DateTimePicker,
  PKR_MAX_SYMBOLS_TOURN_NAME,
  PKR_MIN_SYMBOLS_TOURN_NAME,
  PKR_RESTRICTED_SYMBOLS,
  PKR_RESTRICTED_SYMBOLS_STRING,
  mustBeBiggerOrEqual,
  mustBeLowerOrEqual,
  mustNotContainSymbols,
  useGetCurrentDate,
  useHomeGamesWidget
} from '@swf/poker';

import { HGOnboarding } from '../../../../types/common';
import VideoChat from '../../../CoachMark/CreateGame/VideoChat/VideoChat';
import VideoChatToggle from '../../../Modal/VideoChatToggle/v1/VideoChatToggle';
import useForceUpdate from '../../../Modal/hooks/useForceUpdate';
import CreateTournamentContext from '../../CreateTournamentContext/CreateTournamentContext';
import RecurrenceToggle from '../../Recurrence/Toggle/Toggle';
import useGetDefaultName from '../../hooks/useGetDefaultName';
import useGetGameOptions from '../../hooks/useGetGameOptions';
import canEnableVideo from '../../utils/canEnableVideo';
import type { FormValues } from '../../utils/common';
import getOptionsByCurrency from '../../utils/getOptionsByCurrency';
import getOptionsByHandType from '../../utils/getOptionsByHandType';

import {
  TXTCLI_Buy_In,
  TXTCLI_Currency,
  TXTCLI_Start_Date,
  TXTCLI_Start_Time,
  TXTCLI_Tournament_Name,
  TXTSRV_Game,
  TXTWCL_Must_be_between_N0_and_N1_char_ELL,
  TXTWCL_The_following_characters_are_i_ELL,
  TXTWCL_Type_of_Game
} from './MainStep.messages.json';
import styles from './MainStep.scss';

const startOfTheDay = (date: Date) => format(new Date(date).setHours(0, 0, 0, 0), 'yyyy-MM-dd');
const getTimeFromDate = (date: Date) => `${date.getHours()}:` + `0${date.getMinutes()}`.slice(-2);

const MainStep: React.FC = () => {
  const {
    state: { widgetId }
  } = useContext(CreateTournamentContext);
  const { widgetSelect, widgetDispatch } = useHomeGamesWidget(widgetId);
  const data = useSelector(widgetSelect.tournamentStructures);

  const { change, getState } = useForm<FormValues>();
  const { values } = getState();
  const { handType, tableSize } = values;

  const { formatNumber } = useIntl();

  const createGameOBEnabled = useFeature(FeatureFlag.PKRHomeGamesOnboardingCreateGame);
  const onboardingCompleted = useSelector((rootState: RootState) =>
    widgetSelect?.localStorageItem(rootState, `onboarding[${HGOnboarding.CreateTournament}]`)
  );

  const { greaterThan } = useScreenSize();

  const showCoachMark = createGameOBEnabled && !onboardingCompleted && greaterThan(Size.Small);

  const showIcon = greaterThan(Size.XSmall);
  const getDefaultName = useGetDefaultName();
  const date = new Date();
  const forwardDate = new Date(date.setMinutes(date.getMinutes() + 10));
  const { now, timeZone } = useGetCurrentDate(forwardDate);
  const { locale } = Intl.DateTimeFormat().resolvedOptions();
  const isLocale12h = Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions()?.hour12;
  const altTimeFormat = isLocale12h ? 'h:i K' : 'H:i';
  const {
    input: { value }
  } = useField('startDate', { defaultValue: startOfTheDay(now) });
  useField('startTime', { defaultValue: `${getTimeFromDate(now)}` });
  useField('repeatEvery', { defaultValue: 1 });
  useField('recurringPatternType', {
    defaultValue: 'HG_RECURRING_DAILY' as RecurringTournament['recurringPatternType']
  });
  const isMobileDevice = useSelector(select.device.isMobile);

  const { forceFieldUpdate } = useForceUpdate({ values, change });

  const { handTypeOptions } = useGetGameOptions(data);

  const {
    input: { value: handTypeValue }
  } = useField('handType', {
    subscription: { value: true },
    initialValue: handTypeOptions[0]?.value
  });

  const { potTypeOptions, currencyOptions } = useMemo(() => {
    const { handType, isHiLo } = JSON.parse(handTypeValue);
    return getOptionsByHandType(data, handType, isHiLo);
  }, [data, handTypeValue]);

  const {
    input: { value: potStruct }
  } = useField('potStruct', { initialValue: potTypeOptions[0]?.value });

  const canEnableVideoOption = useMemo(() => {
    const { handType, isHiLo } = JSON.parse(handTypeValue);

    return canEnableVideo(data, handType, isHiLo, potStruct, tableSize);
  }, [data, handTypeValue, tableSize, potStruct]);

  const {
    input: { value: enableVideoChat }
  } = useField('enableVideoChat', {
    subscription: { value: true },
    initialValue: false
  });

  const {
    input: { value: currency }
  } = useField('currency', {
    subscription: { value: true },
    initialValue: currencyOptions[0]?.value
  });

  const buyInOptions = useMemo(
    () => getOptionsByCurrency(data, currency, formatNumber),
    [data, currency, formatNumber]
  );

  useField('buyIn', { initialValue: buyInOptions[0]?.value });

  useEffect(() => {
    const { handType, isHiLo } = JSON.parse(handTypeValue);
    const { tableSizeOptions, potTypeOptions, currencyOptions } = getOptionsByHandType(
      data,
      handType,
      isHiLo
    );

    forceFieldUpdate({ field: 'potStruct', options: potTypeOptions });
    forceFieldUpdate({ field: 'tableSize', options: tableSizeOptions });
    forceFieldUpdate({ field: 'currency', options: currencyOptions });
  }, [data, handTypeValue, forceFieldUpdate]);

  useEffect(() => {
    const buyInOptions = getOptionsByCurrency(data, currency, formatNumber);

    forceFieldUpdate({ field: 'buyIn', options: buyInOptions });
  }, [data, currency, forceFieldUpdate, formatNumber]);

  useEffect(() => {
    if (!canEnableVideoOption && enableVideoChat) {
      change('enableVideoChat', false);
    }
  }, [canEnableVideoOption, enableVideoChat, change]);

  const handleChange = () => {
    change('enableVideoChat', !enableVideoChat);
  };

  return (
    <div className={styles.quickCreate}>
      <Input
        id="gameName"
        placeholder={getDefaultName(handType)}
        label={TXTCLI_Tournament_Name}
        className={styles.input}
        showValidationTick={false}
        validators={[
          mustNotContainSymbols(PKR_RESTRICTED_SYMBOLS, {
            ...TXTWCL_The_following_characters_are_i_ELL,
            values: { 0: PKR_RESTRICTED_SYMBOLS_STRING }
          }),
          mustBeBiggerOrEqual(PKR_MIN_SYMBOLS_TOURN_NAME, {
            ...TXTWCL_Must_be_between_N0_and_N1_char_ELL,
            values: { 0: '6', 1: '30' }
          }),
          mustBeLowerOrEqual(PKR_MAX_SYMBOLS_TOURN_NAME, {
            ...TXTWCL_Must_be_between_N0_and_N1_char_ELL,
            values: { 0: '6', 1: '30' }
          })
        ]}
      />
      {isMobileDevice ? (
        <>
          <Input
            id="startDate"
            name="startDate"
            className={styles.mobilePicker}
            type="date"
            min={startOfTheDay(now)}
            defaultValue={startOfTheDay(now)}
            label={TXTCLI_Start_Date}
          />
          {now && (
            <Input
              id="startTime"
              name="startTime"
              className={styles.mobilePicker}
              type="time"
              min={isSameDay(now, new Date(value)) ? `${getTimeFromDate(now)}` : undefined}
              label={TXTCLI_Start_Time}
            />
          )}
        </>
      ) : (
        <>
          <DateTimePicker
            containerClassName={styles.datePicker}
            id="startDate"
            label={<Translation {...TXTCLI_Start_Date} />}
            className={styles.picker}
            altFormat="j F, l"
            hideDateFormat
            minDate={startOfTheDay(now)}
            defaultDate={startOfTheDay(now)}
            showIcon={showIcon}
            maxDate={add(now, { days: 29, hours: 0, minutes: 0, seconds: 0 })}
          />
          <DateTimePicker
            containerClassName={styles.timePicker}
            id="startTime"
            showIcon={showIcon}
            label={<Translation {...TXTCLI_Start_Time} />}
            className={styles.picker}
            mode="time"
            enableTime
            noCalendar
            time_24hr={!isLocale12h}
            defaultHour={now.getHours()}
            defaultMinute={now.getMinutes()}
            altFormat={altTimeFormat}
            hideDateFormat
            minTime={
              isSameDay(now, new Date(value)) ? `${now.getHours()}:${now.getMinutes()}` : undefined
            }
          >
            {showIcon && <div className={styles.timezone}>{timeZone}</div>}
          </DateTimePicker>
        </>
      )}
      <RecurrenceToggle />
      <div className={styles.mainSettings}>
        <div>
          <div className={styles.handType}>
            <StarsDropDown
              id="handType"
              label={TXTSRV_Game}
              options={handTypeOptions}
              className={styles.dropdown}
            />
          </div>
          <div>
            <StarsDropDown
              id="potStruct"
              label={TXTWCL_Type_of_Game}
              options={potTypeOptions}
              className={styles.dropdown}
            />
          </div>
        </div>
        {showCoachMark ? (
          <CoachMark
            content={
              <VideoChat
                onDismiss={() => {
                  if (!widgetDispatch) return;
                  widgetDispatch.setLocalStorageItem({
                    onboarding: { [HGOnboarding.CreateTournament]: true }
                  });
                }}
              />
            }
            refElementClassName={styles.videoChatToggle}
            placement="left-start"
          >
            <VideoChatToggle
              checked={enableVideoChat}
              supported={canEnableVideoOption}
              onChange={handleChange}
            />
          </CoachMark>
        ) : (
          <VideoChatToggle
            className={styles.videoChatToggle}
            checked={enableVideoChat}
            supported={canEnableVideoOption}
            onChange={handleChange}
          />
        )}
      </div>
      <div>
        <StarsDropDown
          id="currency"
          label={TXTCLI_Currency}
          options={currencyOptions}
          className={styles.dropdown}
        />
      </div>
      <div>
        <StarsDropDown
          id="buyIn"
          label={TXTCLI_Buy_In}
          options={buyInOptions}
          className={styles.dropdown}
        />
      </div>
    </div>
  );
};

export default MainStep;
